import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import AppButton from "../../components/ui/AppButton";

import { formatListDate } from "../../utils/formatters";

import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMEventAllList = ({
  events,
  loading,
  totalElements,
  clubs,
  campuses,
  clubFilter,
  eventStatusFilter,
  campusFilter,
  statusTab,
  actionLoadingId,
  onClubFilterChange,
  onEventStatusFilterChange,
  onCampusFilterChange,
  onStatusTabChange,
  onClearFilters,
  onCreate,
  onView,
  onEdit,
  onCancel,
  onDelete,
  onReactivate,
  onManageTicketAttendants,
}) => {
  const { t, i18n } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();

  const STATUS_LABEL_KEYS = {
    PENDING: "pending",
    UPCOMING: "upcoming",
    PAST: "past",
    CANCELLED: "cancelled",
    REJECTED: "rejected",
  };
  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { color: "warning", icon: "fa-hourglass-half" },
      UPCOMING: { color: "primary", icon: "fa-calendar-plus" },
      PAST: { color: "secondary", icon: "fa-history" },
      CANCELLED: { color: "danger", icon: "fa-ban" },
      REJECTED: { color: "danger", icon: "fa-times-circle" },
    };
    const badge = badges[status] || { color: "secondary", icon: "fa-question-circle" };
    const label = STATUS_LABEL_KEYS[status] ? t(`events.allEvents.list.${STATUS_LABEL_KEYS[status]}`) : status;
    return (
      <span className={`badge bg-${badge.color} bg-opacity-10 text-${badge.color} fw-bold`}>
        <i className={`fas ${badge.icon} me-1`}></i>{label}
      </span>
    );
  };

  const isDeleted = statusTab === false;
  const hasActiveFilters = clubFilter || eventStatusFilter || campusFilter || statusTab === false;

  return (
    <div className="event-all-list">
      <ListToolbar
        title={t("events.allEvents.list.title")}
        subtitle={t("events.allEvents.list.subtitle", { count: totalElements })}
        filterBadges={hasActiveFilters ? (
          <>
            {clubFilter && <span className="badge bg-primary">Club: {clubs.find((c) => c.id.toString() === clubFilter)?.nameLocal || clubs.find((c) => c.id.toString() === clubFilter)?.nameEn}</span>}
            {campusFilter && <span className="badge bg-success">{t("events.allEvents.list.filterByCampus")}: {campuses.find((c) => c.id.toString() === campusFilter)?.nameEn}</span>}
            {eventStatusFilter && <span className="badge bg-info">Status: {eventStatusFilter}</span>}
            {statusTab === false && <span className="badge bg-danger">{t("events.allEvents.list.filterDeleted")}</span>}
          </>
        ) : null}
      >
        <div className="row g-3">
          {can(PERMISSIONS.club.list) && (
            <div className="col-md-3">
              <label className="form-label fw-medium">{t("events.allEvents.list.filterByClub")}</label>
              <select className="form-select" value={clubFilter} onChange={(e) => onClubFilterChange(e.target.value)}>
                <option value="">{t("events.allEvents.list.allClubs")}</option>
                {clubs.map((club) => <option key={club.id} value={club.id}>{club.name || club.nameLocal || club.nameEn}</option>)}
              </select>
            </div>
          )}
          {can(PERMISSIONS.campus.list) && (
            <div className="col-md-3">
              <label className="form-label fw-medium">{t("events.allEvents.list.filterByCampus")}</label>
              <select className="form-select" value={campusFilter} onChange={(e) => onCampusFilterChange(e.target.value)}>
                <option value="">{t("events.allEvents.list.allCampuses")}</option>
                {campuses.map((campus) => <option key={campus.id} value={campus.id}>{campus.nameEn || campus.nameLocal}</option>)}
              </select>
            </div>
          )}
          <div className="col-md-3">
            <label className="form-label fw-medium">{t("events.allEvents.list.eventStatus")}</label>
            <select className="form-select" value={eventStatusFilter} onChange={(e) => onEventStatusFilterChange(e.target.value)}>
              <option value="">{t("events.allEvents.list.allStatuses")}</option>
              <option value="PENDING">{t("events.allEvents.list.pending")}</option>
              <option value="UPCOMING">{t("events.allEvents.list.upcoming")}</option>
              <option value="PAST">{t("events.allEvents.list.past")}</option>
              <option value="CANCELLED">{t("events.allEvents.list.cancelled")}</option>
              <option value="REJECTED">{t("events.allEvents.list.rejected")}</option>
            </select>
          </div>
          <div className="col-md-3 d-flex align-items-end justify-content-md-end gap-2 flex-wrap">
            {hasActiveFilters && (
              <AppButton variant="outline" className="btn-sm" onClick={onClearFilters}>
                <i className="fas fa-times me-1"></i>{tCommon("buttons.clearFilters")}
              </AppButton>
            )}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${statusTab === true ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => onStatusTabChange(true)}
              >
                <i className="fas fa-check-circle me-1"></i>
                {t("events.allEvents.list.filterActive")}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusTab === false ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => onStatusTabChange(false)}
              >
                <i className="fas fa-trash-alt me-1"></i>
                {t("events.allEvents.list.filterDeleted")}
              </button>
            </div>
            {!isDeleted && can(PERMISSIONS.event.create) && (
              <AppButton variant="primary" className="btn-sm" onClick={onCreate}>
                <i className="fas fa-plus me-1"></i>
                {t("events.allEvents.list.addEvent")}
              </AppButton>
            )}
          </div>
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{tCommon("actions.loading")}</span></div>
              <p className="text-muted mt-2">{t("events.allEvents.list.loading")}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-calendar-times text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">{t("events.allEvents.list.noFound")}</h5>
              <p className="text-muted">{hasActiveFilters ? t("events.allEvents.list.adjustFilters") : t("events.allEvents.list.noneCreated")}</p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("events.allEvents.list.columns.event")}</th>
                  <th>{t("events.allEvents.list.columns.club")}</th>
                  <th>{t("events.allEvents.list.columns.date")}</th>

                  <th className="text-center">{t("events.allEvents.list.columns.attendance")}</th>
                  <th className="text-center">{t("events.allEvents.list.columns.status")}</th>
                  <th className="text-center pe-4">{t("events.allEvents.list.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  const thumbnailUrl = event.imageUrl;
                  return (
                  <tr key={event.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt=""
                            className="rounded-circle me-3 flex-shrink-0"
                            style={{ width: "45px", height: "45px", objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="event-icon rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{ width: "45px", height: "45px" }}
                          >
                            <i className="fas fa-calendar-alt text-primary"></i>
                          </div>
                        )}
                        <div>
                          <div className="fw-bold text-dark">{event.name || event.titleLocal || event.titleEn}</div>
                          {event.titleLocal && event.titleEn && <small className="text-muted">{event.titleEn}</small>}
                        </div>
                      </div>
                    </td>
                    <td>
                      {event.isUniversityEvent ? (
                        <span className="badge bg-secondary bg-opacity-10 text-secondary">
                          <i className="fas fa-university me-1"></i>
                          {t("events.allEvents.list.universityEvent")}
                        </span>
                      ) : (
                        <div className="fw-medium text-dark">{event.clubName || event.clubNameLocal || event.clubNameEn}</div>
                      )}
                      <small className="text-muted d-block">
                        {event.campusName || event.campusNameLocal || event.campusNameEn}
                        {(event.universityName || event.universityNameLocal || event.universityNameEn) ? `, ${event.universityName || event.universityNameLocal || event.universityNameEn}` : ""}
                      </small>
                    </td>
                    <td>
                      <span className="text-muted">{formatListDate(event.startDate, i18n.language)}</span>
                    </td>

                    <td className="text-center">
                      <span className="text-dark">
                        {event.attendeeCount ?? 0}{event.quota != null ? ` / ${event.quota}` : ""}
                      </span>
                      {event.isFull && (
                        <span className="badge bg-danger bg-opacity-10 text-danger fw-bold ms-1">{tCommon("status.full", "Full")}</span>
                      )}
                    </td>
                    <td className="text-center">{getStatusBadge(event.eventStatus)}</td>
                    <td className="text-center pe-4">
                      {isDeleted ? (
                        can(PERMISSIONS.event.update) && (
                          <AppButton
                            variant="outline"
                            className="btn-sm text-success border-success"
                            onClick={() => onReactivate(event)}
                            disabled={actionLoadingId === event.id}
                            title={t("events.allEvents.list.reactivate")}
                          >
                            {actionLoadingId === event.id ? (
                              <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                              <>
                                <i className="fas fa-undo me-1"></i>
                                {t("events.allEvents.list.reactivate")}
                              </>
                            )}
                          </AppButton>
                        )
                      ) : (
                        <div className="btn-group" role="group">
                          {can(PERMISSIONS.event.get) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-info border-info"
                              onClick={() => onView(event)}
                              title={tCommon("buttons.viewDetails")}
                            >
                              <i className="fas fa-eye"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.event.update) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-primary border-primary"
                              onClick={() => onEdit(event)}
                              title={tCommon("buttons.edit")}
                            >
                              <i className="fas fa-edit"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.event.attendantList) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-secondary border-secondary"
                              onClick={() => onManageTicketAttendants(event)}
                              title={t("events.allEvents.list.manageTicketAttendants")}
                            >
                              <i className="fas fa-id-badge"></i>
                            </AppButton>
                          )}
                          {event.eventStatus !== "CANCELLED" && event.eventStatus !== "PAST" && can(PERMISSIONS.event.cancel) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-warning border-warning"
                              onClick={() => onCancel(event)}
                              disabled={actionLoadingId === event.id}
                              title={t("events.allEvents.list.cancelEvent")}
                            >
                              <i className="fas fa-ban"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.event.delete) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-danger border-danger"
                              onClick={() => onDelete(event)}
                              disabled={actionLoadingId === event.id}
                              title={tCommon("buttons.delete")}
                            >
                              {actionLoadingId === event.id ? (
                                <span className="spinner-border spinner-border-sm"></span>
                              ) : (
                                <i className="fas fa-trash"></i>
                              )}
                            </AppButton>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMEventAllList;
